import { useEffect, useState, useRef, useContext } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import {
  getQueryParam,
  sortByOrderDateTime,
  formatPhoneNumber,
  capitalize,
  getFormattedAddressAsArray,
  getCustomerName,
  equalsIgnoreCase,
  getStoreFilterStreets,
  orderMatchesStoreFilter,
} from "../../common/util";
import bkstApi from "../../api";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router";
import BDatePicker from "../../common/input/BDatePicker";
import SearchIcon from "@mui/icons-material/Search";
import FulfillmentSummary from "../component/FulfillmentSummary";
import FulfillmentSlip from "../component/FulfillmentSlip";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import currency from "currency.js";
import { Link as RouterLink } from "react-router-dom";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import ItemFilter from "../component/ItemFilter";
import DSPLogo from "../../common/DSPLogo";
import { UserContext } from "../../context/UserContext";

export default function FulfillmentPlanner(props) {
  const { user } = useContext(UserContext);

  const navigate = useNavigate();
  let location = useLocation();

  const [fromDt, setFromDt] = useState("");
  const [toDt, setToDt] = useState("");

  const [orders, setOrders] = useState([]);

  const [filter, setFilter] = useState();
  const [filteredResults, setFilteredResults] = useState([]);

  const [loading, setLoading] = useState(true);
  const storeLocations = user?.config?.storeLocation || [];
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;
  const assignedStoreIds = Array.isArray(user?.storeLocationIds)
    ? user.storeLocationIds.filter(Boolean)
    : [];
  const showStreetFallback = assignedStoreIds.length === 0;

  const printRef = useRef();

  const print = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `Fulfillment Report`,
  });

  const handlePrint = () => {
    print();
  };

  // Check params to identify search criteria
  useEffect(() => {
    let [qFrom, qTo] = [
      getQueryParam(window.location.search, "from"),
      getQueryParam(window.location.search, "to"),
    ];
    if (!qFrom || !qTo) {
      // reset dates
      qFrom = moment().format("YYYY-MM-DD");
      qTo = qFrom;
    }
    search(qFrom, qTo);
  }, [location.key]);

  useEffect(() => {
    if (toDt < fromDt) {
      setToDt("");
    }
  }, [fromDt]);

  /*useEffect(() => {
    if (fulfillmentType === "delivery") {
      setStoreLocation("");
    }
    setFilteredResults(
      orders.filter((o) => {
        if (fulfillmentType === "delivery") {
          return o.fulfillment.type === fulfillmentType;
        } else if (fulfillmentType === "pickup") {
          if (storeLocation) {
            return (o.fulfillment.location.street || o.fulfillment.address || "").includes(storeLocation);
          } else {
            return o.fulfillment.type === fulfillmentType;
          }
        }
      })
    );
  }, [fulfillmentType, storeLocation]);*/

  const runDateSearch = (e) => {
    e.preventDefault();
    navigate(`/planning/fulfillment?from=${fromDt}&to=${toDt}`);
  };

  const search = (fromDate, toDate) => {
    setLoading(true);

    bkstApi.get(`/my/planning/fulfillment?fromDt=${fromDate}&toDt=${toDate}`).then((res) => {
      const sortedOrders = res.data.orders.sort(sortByOrderDateTime);

      setOrders(sortedOrders);
      setFilteredResults(sortedOrders);

      let [count, sales] = [0, 0];
      for (const o of res.data.orders) {
        count++;
        sales += +o.payment.total;
      }

      setFromDt(fromDate);
      setToDt(toDate);

      setLoading(false);
    });
  };

  useEffect(() => {
    let arr = [];
    arr = orders.filter((o) => {
      // check filters
      {
        for (const [k, v] of Object.entries(filter || {})) {
          if (v.value) {
            if (k === "fulfillment" && !equalsIgnoreCase(o.fulfillment.type, v.value)) {
              return false;
            } else if (k === "store") {
              if (!orderMatchesStoreFilter(o, v.value, storeLocations)) {
                return false;
              }
            } else if (k === "location") {
              if (o.fulfillment.location) {
                if (!equalsIgnoreCase(o.fulfillment.location.street, v.value)) {
                  return false;
                }
              } else if (!o.fulfillment.address?.toLowerCase().startsWith(v.value?.toLowerCase())) {
                return false;
              }
            } else if (
              k === "provider" &&
              !equalsIgnoreCase(o.fulfillment.provider?.name || "in-house", v.value)
            ) {
              return false;
            }
          }
        }
      }
      // passed all filters
      return true;
    });
    setFilteredResults(arr);
  }, [filter, orders]);

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Container maxWidth="md">
        <Box>
          <Box my={5}>
            <Typography variant="h1" gutterBottom>
              Fulfillment Plan
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Filter orders by fulfillment type (pickup, delivery), store location & delivery
              provider
            </Typography>
            <Box py={2} my={2} sx={{ backgroundColor: "#FFFFFF", borderRadius: "4px" }}>
              <form onSubmit={runDateSearch}>
                <Box pt={1} align="center" sx={{ display: "flex", justifyContent: "center" }}>
                  <BDatePicker
                    value={fromDt}
                    label="From Date"
                    onChange={(d) => setFromDt(d)}
                    required
                    autoOk
                    hideIcon={true}
                  />
                  <BDatePicker
                    value={toDt}
                    label="To Date"
                    onChange={(d) => setToDt(d)}
                    required
                    autoOk
                    hideIcon={true}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{ height: "56px" }}
                  >
                    <SearchIcon />
                  </Button>
                </Box>
              </form>
            </Box>
            {orders && orders.length > 0 && (
              <Box>
                <Box my={2}>
                  <ItemFilter
                    config={[
                      { name: "fulfillment", options: orders.map((o) => o.fulfillment?.type) },
                      showStoreFilter ? { name: "store", options: storeStreets } : "",
                      showStreetFallback && filter?.["fulfillment"]?.value === "pickup"
                        ? {
                            name: "location",
                            options: orders.map(
                              (o) =>
                                o.fulfillment.type === "pickup" && o.fulfillment.location?.street
                            ),
                          }
                        : "",
                      filter?.["fulfillment"]?.value === "delivery"
                        ? {
                            name: "provider",
                            options: orders.map(
                              (o) =>
                                o.fulfillment.type === "delivery" && o.fulfillment.provider?.name
                            ),
                          }
                        : "",
                    ]}
                    data={orders}
                    onChange={(o) => {
                      let tmp = { ...filter, ...o };

                      if (tmp["location"]?.value && tmp["fulfillment"]?.value !== "pickup") {
                        delete tmp["location"];
                      }
                      if (tmp["provider"]?.value) {
                        if (tmp["fulfillment"]?.value !== "delivery") {
                          delete tmp["provider"];
                        }
                      }

                      setFilter(tmp);
                    }}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        {!loading && (
          <Box>
            {(!orders || orders.length === 0) && (
              <Alert severity="error">No orders found within selected date range</Alert>
            )}
            {orders && orders.length > 0 && (
              <Box>
                <Box p={1} my={2} sx={{ backgroundColor: "#ECECEC" }}>
                  <Grid container alignItems={"center"}>
                    <Grid item xs={6}>
                      <Typography variant="overline" color="textSecondary">
                        showing {filteredResults.length} orders.{" "}
                        {filter?.fulfillment === "delivery"
                          ? `Total Delivery Fee: ${currency(
                              filteredResults.reduce((acc, o) => {
                                return +acc + +o.payment.delivery;
                              }, 0)
                            ).format()}`
                          : ""}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} textAlign={"right"}>
                      {/* <IconButton>
                        <PrintOutlinedIcon />
                    </IconButton>*/}
                    </Grid>
                  </Grid>
                </Box>
                <Box>
                  {filteredResults.map((o, idx) => {
                    let address = getFormattedAddressAsArray(o.fulfillment.location, true);
                    if (address.length === 0) {
                      const addressArr = o.fulfillment.address.split(",");
                      const addressLine1 = addressArr.shift();
                      const addressLine2 = addressArr.join();

                      address.push(addressLine1);
                      address.push(addressLine2);
                    }

                    return (
                      <Paper elevation={0}>
                        <Box p={2} pr={0} mb={2}>
                          <Grid key={idx} container spacing={1}>
                            <Grid item sm={2} xs={12}>
                              <Box sx={{ display: "flex", gap: "7px" }}>
                                {o.fulfillment.type === "pickup" ? (
                                  <LocalMallOutlinedIcon sx={{ color: "#999" }} />
                                ) : (
                                  <LocalShippingOutlinedIcon sx={{ color: "#999" }} />
                                )}

                                <Box>
                                  <Typography variant="body2">
                                    {moment(o.fulfillment.date).format("ddd, MMM D")}
                                  </Typography>
                                  <Typography variant="overline" color="textSecondary">
                                    {o.fulfillment.time}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                            <Grid item sm={3} xs={12}>
                              {address.length > 0 && (
                                <Typography variant="body2" display="block">
                                  {address[0]}
                                </Typography>
                              )}
                              {address.length > 1 && (
                                <Typography variant="body2" display="block">
                                  {address[1]}
                                </Typography>
                              )}
                            </Grid>
                            <Grid item sm={3} xs={12}>
                              <Typography variant="body2">
                                {capitalize(getCustomerName(o.customer) || "")}
                              </Typography>
                              <Typography variant="overline" color="textSecondary">
                                {formatPhoneNumber(o.customer.phone)}
                              </Typography>
                            </Grid>
                            <Grid item sm={2} xs={10}>
                              {o.fulfillment.type === "delivery" && (
                                <Box sx={{ display: "flex", gap: "7px" }}>
                                  <DSPLogo siteId={user.acctId} provider={o.fulfillment.provider} />
                                  <Box>
                                    <Typography variant="overline">
                                      {o.fulfillment.provider
                                        ? o.fulfillment.provider.name
                                        : "In-House"}
                                    </Typography>
                                    <Typography
                                      variant="overline"
                                      color="textSecondary"
                                      display="block"
                                    >{`${currency(o.payment.delivery).format()}`}</Typography>
                                  </Box>
                                </Box>
                              )}
                              <Typography variant="caption" color="textSecondary" display="block">
                                {o.fulfillment.instructions}
                              </Typography>
                            </Grid>
                            <Grid item sm={2} xs={2} textAlign={"right"}>
                              <IconButton component={RouterLink} to={`/order/${o.id}`}>
                                <VisibilityOutlinedIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Box>
                      </Paper>
                    );
                  })}
                  {/* <DataGrid
              sx={{
                "& .MuiDataGrid-cell": {
                  padding: "10px"
                }
              }}
              disableColumnFilter
              rows={orders.map((o) => {
                const addressArr = o.fulfillment.address.split(",");
                const addressLine1 = addressArr.shift();
                const addressLine2 = addressArr.join();

                return {
                  id: o.id,
                  date: o.fulfillment.date,
                  time: o.fulfillment.time,
                  addressLine1,
                  addressLine2,
                  instructions: o.fulfillment.instructions,
                  name: getCustomerName(o.customer),
                  phone: o.customer.phone,
                  deliveryFee: o.payment.delivery,
                  description: o.details.title
                };
              })}
              columns={columns}
              autoHeight
              getRowHeight={() => "auto"}
              disableRowSelectionOnClick
              initialState={{
                sorting: {
                  sortModel: [{ field: "time", sort: "desc" }]
                }
              }}
            /> */}
                  {filter?.fulfillment && (
                    <Box style={{ overflow: "hidden", height: "0" }}>
                      <Box ref={printRef}>
                        <FulfillmentSummary
                          siteId={user.acctId}
                          orders={filteredResults}
                          fulfillmentType={filter?.fulfillment}
                        />
                        <div class="pagebreak"> </div>
                        {filteredResults.map((o, idx) => {
                          return (
                            <Box>
                              <FulfillmentSlip siteId={user.acctId} order={o} />
                              {filteredResults.length > +idx + 1 && <div class="pagebreak"> </div>}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )}
                  {filter?.fulfillment && filteredResults.length > 0 && (
                    <Box my={2} textAlign="center">
                      <Button
                        size="large"
                        fullWidth
                        startIcon={<PrintOutlinedIcon />}
                        variant="contained"
                        color="primary"
                        onClick={handlePrint}
                      >
                        PRINT
                      </Button>
                      <Typography variant="caption" color="textSecondary">
                        Print summary of {capitalize(filter?.fulfillment)} orders, plus{" "}
                        {capitalize(filter?.fulfillment)} slips for each order.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Container>
    );
  }
}
